import { TabSection } from './TabSection'
import { ChipInput } from '../ChipInput'
import { FrozenMemberList } from '../FrozenMemberList'
import { Icon } from '../Icon'
import type { ChannelConfigUpdate, FilterMode } from '../../types'
import noteStyles from './FiltersTab.module.css'

function normalizeRepo(repo: string): string {
  const trimmed = repo.trim().toLowerCase()
  return trimmed.includes('/') ? trimmed : `toasttab/${trimmed}`
}

function matchesGlob(pattern: string, value: string): boolean {
  const starIdx = pattern.indexOf('*')
  if (starIdx === -1) return pattern === value
  const prefix = pattern.slice(0, starIdx)
  const suffix = pattern.slice(starIdx + 1)
  return value.startsWith(prefix) && value.endsWith(suffix)
}

function repoFilteredOutWarning(
  repo: string,
  mode: FilterMode | null | undefined,
  patterns: string[] | undefined
): string | undefined {
  if (!mode || !patterns || patterns.length === 0) return undefined
  const value = normalizeRepo(repo)
  const matched = patterns.some(p => matchesGlob(normalizeRepo(p), value))
  const filteredOut = mode === 'ALLOWLIST' ? !matched : matched
  return filteredOut ? 'This repository is filtered out below, so you won\'t get its PRs' : undefined
}

interface Props {
  draft: ChannelConfigUpdate & {
    githubTeamSlugs: string[]
    authorTeamSlugs: string[]
    watchedAuthorLogins: string[]
    watchedReviewerLogins: string[]
    watchedRepos: string[]
  }
  authorTeamMemberLogins?: Record<string, string[]> | null
  onChange: (patch: Partial<ChannelConfigUpdate>) => void
}

export function GeneralTab({ draft, authorTeamMemberLogins, onChange }: Props) {
  return (
    <TabSection
      accent="blue"
      icon="person_search"
      title="Subscriptions"
      subtitle="Choose who or what brings a PR into this channel — by team, by user, or by repository"
    >
      <ChipInput
        label="Authoring teams"
        values={draft.authorTeamSlugs}
        onChange={v => onChange({ authorTeamSlugs: v })}
        placeholder="e.g. enterprise-away — must match your GitHub team name exactly"
        hint="Get notified when a member of these GitHub teams opens a PR, on any repo they touch — regardless of who's asked to review it"
      />

      <ChipInput
        label="Reviewing teams"
        values={draft.githubTeamSlugs}
        onChange={v => onChange({ githubTeamSlugs: v })}
        placeholder="e.g. platform-eng — must match your GitHub team name exactly"
        hint="Get notified when a PR requests review from any of these GitHub teams"
      />

      <ChipInput
        label="Authoring users"
        values={draft.watchedAuthorLogins}
        onChange={v => onChange({ watchedAuthorLogins: v })}
        placeholder="e.g. x-toast"
        hint="Get notified when any of these specific GitHub users opens a PR"
      />

      {authorTeamMemberLogins && Object.keys(authorTeamMemberLogins).length > 0 && (
        <FrozenMemberList
          teamMemberLogins={authorTeamMemberLogins}
          label="Via author team subscriptions"
        />
      )}

      <ChipInput
        label="Reviewing users"
        values={draft.watchedReviewerLogins}
        onChange={v => onChange({ watchedReviewerLogins: v })}
        placeholder="e.g. x-toast"
        hint="Get notified when a review is explicitly requested from any of these specific GitHub users"
      />

      <ChipInput
        label="Repositories"
        values={draft.watchedRepos}
        onChange={v => onChange({ watchedRepos: v })}
        placeholder="e.g. toasttab/payments-api, payments-api, or a GitHub link"
        hint="Get notified for every PR opened in these repositories, regardless of author or reviewer — your other filters still apply"
        warningFor={repo => repoFilteredOutWarning(repo, draft.repoFilterMode, draft.filteredRepos)}
      />

      <div className={noteStyles.notes}>
        <div className={noteStyles.note}>
          <Icon name="info" size="sm" className={noteStyles.noteIcon} />
          If a PR matches more than one subscription above, you'll only be notified once.
        </div>
      </div>
    </TabSection>
  )
}
